import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, Button, Input } from '../components/Common';

const EVENT_TYPES = [
  { key: 'critical_finding', label: 'Critical Finding' },
  { key: 'engagement_status_change', label: 'Engagement Status Change' },
  { key: 'report_generated', label: 'Report Generated' },
  { key: 'engagement_assigned', label: 'Assigned to an Engagement' },
  { key: 'engagement_created', label: 'Engagement Created' },
  { key: 'report_ready_for_review', label: 'Ready for Technical Review' },
  { key: 'finding_retest', label: 'Vulnerability Set to Retest' }
];

const CHANNEL_TYPES = ['in_app', 'email', 'slack'];

const CHANNEL_LABELS = { in_app: 'In-App', email: 'Email', slack: 'Slack' };

export default function NotificationSettings() {
  const { apiFetch, hasGroup } = useAuth();
  const isAdmin = hasGroup('Super Admins');

  const [preferences, setPreferences] = useState({});
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');

  // Channel management state (admin)
  const [editingChannel, setEditingChannel] = useState(null);
  const [channelForm, setChannelForm] = useState({ channel_type: 'email', name: '', config: {} });
  const [channelSaving, setChannelSaving] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [prefRes, chanRes] = await Promise.all([
        apiFetch('/notifications/preferences'),
        isAdmin ? apiFetch('/notifications/channels') : Promise.resolve(null)
      ]);

      const prefData = await prefRes.json();
      // Build a map: { event_type: { channel_type: is_enabled } }
      const prefMap = {};
      for (const et of EVENT_TYPES) {
        prefMap[et.key] = {};
        for (const ct of CHANNEL_TYPES) {
          prefMap[et.key][ct] = true; // default enabled
        }
      }
      for (const p of prefData) {
        if (prefMap[p.event_type]) {
          prefMap[p.event_type][p.channel_type] = p.is_enabled;
        }
      }
      setPreferences(prefMap);

      if (chanRes && chanRes.ok) {
        setChannels(await chanRes.json());
      }
    } catch (err) {
      console.error('Failed to load notification settings:', err);
    }
    setLoading(false);
  }, [apiFetch, isAdmin]);

  useEffect(() => { loadData(); }, [loadData]);

  const togglePreference = (eventType, channelType) => {
    setPreferences(prev => ({
      ...prev,
      [eventType]: {
        ...prev[eventType],
        [channelType]: !prev[eventType][channelType]
      }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveMsg('');
    try {
      const body = [];
      for (const et of EVENT_TYPES) {
        for (const ct of CHANNEL_TYPES) {
          body.push({
            event_type: et.key,
            channel_type: ct,
            is_enabled: preferences[et.key]?.[ct] ?? true
          });
        }
      }
      const res = await apiFetch('/notifications/preferences', {
        method: 'PUT',
        body: JSON.stringify(body)
      });
      if (res.ok) {
        setSaveMsg('Preferences saved successfully');
        setTimeout(() => setSaveMsg(''), 3000);
      }
    } catch {
      setSaveMsg('Failed to save preferences');
    }
    setSaving(false);
  };

  const handleSaveChannel = async () => {
    setChannelSaving(true);
    try {
      const isEdit = !!editingChannel;
      const url = isEdit
        ? `/notifications/channels/${editingChannel}`
        : '/notifications/channels';
      const res = await apiFetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        body: JSON.stringify(channelForm)
      });
      if (res.ok) {
        setEditingChannel(null);
        setChannelForm({ channel_type: 'email', name: '', config: {} });
        loadData();
      }
    } catch {}
    setChannelSaving(false);
  };

  const startEditChannel = (ch) => {
    setEditingChannel(ch.id);
    setChannelForm({
      channel_type: ch.channel_type,
      name: ch.name,
      config: ch.config || {}
    });
  };

  const styles = {
    page: { maxWidth: 900 },
    title: { fontSize: 24, fontWeight: 700, color: 'var(--text)', marginBottom: 8 },
    subtitle: { fontSize: 14, color: 'var(--text-secondary)', marginBottom: 24 },
    sectionTitle: { fontSize: 18, fontWeight: 600, color: 'var(--text)', marginBottom: 16, marginTop: 32 },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: {
      padding: '12px 16px', textAlign: 'left', fontSize: 13, fontWeight: 600,
      color: 'var(--text-secondary)', borderBottom: '2px solid var(--border)',
      background: 'var(--bg-input)'
    },
    td: {
      padding: '12px 16px', borderBottom: '1px solid var(--border)',
      fontSize: 14, color: 'var(--text)'
    },
    toggle: (enabled) => ({
      width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer',
      background: enabled ? '#3b82f6' : 'var(--bg-input)',
      position: 'relative', transition: 'background 0.2s', flexShrink: 0,
      outline: enabled ? '2px solid rgba(59,130,246,0.3)' : 'none'
    }),
    toggleKnob: (enabled) => ({
      width: 18, height: 18, borderRadius: '50%', background: 'white',
      position: 'absolute', top: 3, left: enabled ? 23 : 3,
      transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
    }),
    successMsg: {
      padding: '10px 14px', borderRadius: 8, background: 'rgba(34,197,94,0.1)',
      color: '#22c55e', border: '1px solid rgba(34,197,94,0.2)', marginBottom: 16, fontSize: 14
    },
    channelCard: {
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '14px 16px', borderRadius: 8, border: '1px solid var(--border)',
      background: 'var(--bg-input)', marginBottom: 8
    },
    channelInfo: { flex: 1 },
    channelName: { fontSize: 14, fontWeight: 600, color: 'var(--text)' },
    channelType: { fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 },
    channelStatus: (active) => ({
      display: 'inline-block', padding: '2px 8px', borderRadius: 12, fontSize: 11,
      fontWeight: 600, background: active ? 'rgba(34,197,94,0.15)' : 'rgba(107,114,128,0.15)',
      color: active ? '#22c55e' : '#6b7280', marginLeft: 8
    }),
    formRow: { display: 'flex', gap: 12, marginBottom: 0 }
  };

  if (loading) {
    return (
      <div style={styles.page}>
        <h1 style={styles.title}>Notification Settings</h1>
        <Card><p style={{ color: 'var(--text-secondary)' }}>Loading...</p></Card>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <h1 style={styles.title}>Notification Settings</h1>
      <p style={styles.subtitle}>
        Choose how you want to be notified for different events.
      </p>

      {saveMsg && <div style={styles.successMsg}>{saveMsg}</div>}

      <Card>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Event</th>
              {CHANNEL_TYPES.map(ct => (
                <th key={ct} style={{ ...styles.th, textAlign: 'center', width: 100 }}>
                  {CHANNEL_LABELS[ct]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {EVENT_TYPES.map(et => (
              <tr key={et.key}>
                <td style={styles.td}>{et.label}</td>
                {CHANNEL_TYPES.map(ct => (
                  <td key={ct} style={{ ...styles.td, textAlign: 'center' }}>
                    <button
                      style={styles.toggle(preferences[et.key]?.[ct] ?? true)}
                      onClick={() => togglePreference(et.key, ct)}
                    >
                      <div style={styles.toggleKnob(preferences[et.key]?.[ct] ?? true)} />
                    </button>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Preferences'}
          </Button>
        </div>
      </Card>

      {isAdmin && (
        <>
          <h2 style={styles.sectionTitle}>Notification Channels</h2>
          <Card>
            {channels.map(ch => (
              <div key={ch.id} style={styles.channelCard}>
                <div style={styles.channelInfo}>
                  <div style={styles.channelName}>
                    {ch.name}
                    <span style={styles.channelStatus(ch.is_active)}>
                      {ch.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div style={styles.channelType}>
                    {CHANNEL_LABELS[ch.channel_type] || ch.channel_type}
                    {ch.channel_type === 'email' && ch.config?.smtp_host && ` — ${ch.config.smtp_host}`}
                    {ch.channel_type === 'slack' && ch.config?.webhook_url && ' — Webhook configured'}
                  </div>
                </div>
                <Button variant="secondary" style={{ padding: '6px 12px', fontSize: 12 }}
                  onClick={() => startEditChannel(ch)}>
                  Edit
                </Button>
              </div>
            ))}

            {channels.length === 0 && (
              <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 16 }}>
                No channels configured yet.
              </p>
            )}

            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16, marginTop: 16 }}>
              <h3 style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', marginBottom: 12 }}>
                {editingChannel ? 'Edit Channel' : 'Add Channel'}
              </h3>
              <div style={styles.formRow}>
                <div style={{ flex: 1 }}>
                  <Input label="Channel Name" value={channelForm.name}
                    onChange={e => setChannelForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="e.g., Company Email" />
                </div>
                {!editingChannel && (
                  <div style={{ width: 150 }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: 'var(--text-secondary)' }}>Type</label>
                    <select
                      style={{
                        width: '100%', padding: '10px 14px', borderRadius: 'var(--radius)',
                        border: '1px solid var(--border)', background: 'var(--bg-input)',
                        color: 'var(--text)', fontSize: 14, marginBottom: 16
                      }}
                      value={channelForm.channel_type}
                      onChange={e => setChannelForm(f => ({ ...f, channel_type: e.target.value, config: {} }))}
                    >
                      <option value="email">Email</option>
                      <option value="slack">Slack</option>
                    </select>
                  </div>
                )}
              </div>

              {(channelForm.channel_type === 'email') && (
                <>
                  <div style={styles.formRow}>
                    <div style={{ flex: 2 }}>
                      <Input label="SMTP Host" value={channelForm.config?.smtp_host || ''}
                        onChange={e => setChannelForm(f => ({ ...f, config: { ...f.config, smtp_host: e.target.value } }))}
                        placeholder="smtp.example.com" />
                    </div>
                    <div style={{ flex: 1 }}>
                      <Input label="SMTP Port" type="number" value={channelForm.config?.smtp_port || ''}
                        onChange={e => setChannelForm(f => ({ ...f, config: { ...f.config, smtp_port: parseInt(e.target.value) || '' } }))}
                        placeholder="587" />
                    </div>
                  </div>
                  <div style={styles.formRow}>
                    <div style={{ flex: 1 }}>
                      <Input label="SMTP User" value={channelForm.config?.smtp_user || ''}
                        onChange={e => setChannelForm(f => ({ ...f, config: { ...f.config, smtp_user: e.target.value } }))}
                        placeholder="user@example.com" />
                    </div>
                    <div style={{ flex: 1 }}>
                      <Input label="SMTP Password" type="password" value={channelForm.config?.smtp_pass || ''}
                        onChange={e => setChannelForm(f => ({ ...f, config: { ...f.config, smtp_pass: e.target.value } }))} />
                    </div>
                  </div>
                  <Input label="From Address" value={channelForm.config?.smtp_from || ''}
                    onChange={e => setChannelForm(f => ({ ...f, config: { ...f.config, smtp_from: e.target.value } }))}
                    placeholder="noreply@example.com" />
                </>
              )}

              {channelForm.channel_type === 'slack' && (
                <Input label="Webhook URL" value={channelForm.config?.webhook_url || ''}
                  onChange={e => setChannelForm(f => ({ ...f, config: { ...f.config, webhook_url: e.target.value } }))}
                  placeholder="https://hooks.slack.com/services/..." />
              )}

              <div style={{ display: 'flex', gap: 8 }}>
                <Button onClick={handleSaveChannel} disabled={channelSaving || !channelForm.name}>
                  {channelSaving ? 'Saving...' : editingChannel ? 'Update Channel' : 'Add Channel'}
                </Button>
                {editingChannel && (
                  <Button variant="secondary" onClick={() => {
                    setEditingChannel(null);
                    setChannelForm({ channel_type: 'email', name: '', config: {} });
                  }}>Cancel</Button>
                )}
              </div>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
